import { MediaSize } from '../../components/organisms/MediaInformation/Constants';

export type MediaSize = 'large' | 'medium' | 'small';
export type Orientation = 'portrait' | 'landscape';

export const ImageState = {
  pending: 'pending',
  ready: 'ready',
  defaultImage: 'defaultImage',
} as const;

export type ImageState = (typeof ImageState)[keyof typeof ImageState];

export const ImageTypes = {
  profileImage: 'profileImage',
  profileImageSmall: 'profileImageSmall',
  portalAvatar: 'portalAvatar',
  portalLogo: 'portalLogo',
  titleImage: 'titleImage',
  teaserImage: 'teaserImage',
  contentImage: 'contentImage',
  topHeadlineImage: 'topHeadlineImage',
  imageGalleryImage: 'imageGalleryImage',
  topHeadlineImageSmall: 'topHeadlineImageSmall',
  appLibraryImage: 'appLibraryImage',
  categoryImage: 'categoryImage',
} as const;

interface ImageBase {
  contentType: 'image/jpeg' | 'image/png'; // if this changes in backend change it here!
  src: string;
  availableWidths: number[];
  alt?: string;
  copyright?: string;
  imageState: ImageState;
  orientation: Orientation;
}

interface PortalImageBase {
  contentType: 'image/jpeg' | 'image/png';
  src: string;
  imageState: ImageState;
  orientation: Orientation;
  availableWidths: number[];
}

interface ContentImageBase extends ImageBase {
  caption: string;
  size: MediaSize;
  popUp: boolean;
}

export interface PortalAvatar extends PortalImageBase {
  imageType: typeof ImageTypes.portalAvatar;
}

export interface PortalLogo extends PortalImageBase {
  imageType: typeof ImageTypes.portalLogo;
}

export interface ProfileImage extends PortalImageBase {
  imageType: typeof ImageTypes.profileImage;
}

export interface ProfileImageSmall extends PortalImageBase {
  imageType: typeof ImageTypes.profileImageSmall;
}

export interface TeaserImage extends ContentImageBase {
  imageType: typeof ImageTypes.teaserImage;
}

export interface TitleImage extends ContentImageBase {
  imageType: typeof ImageTypes.titleImage;
}

export interface ContentImage extends ContentImageBase {
  imageType: typeof ImageTypes.contentImage;
}

export interface ImageGallery extends ContentImageBase {
  imageType: typeof ImageTypes.imageGalleryImage;
  originalFilename: string;
  imageId: string;
}

export interface TopHeadlineImage extends ImageBase {
  originalFilename?: string;
  imageId: string;
  imageType: typeof ImageTypes.topHeadlineImage;
}

export interface TopHeadlineImageSmall extends ImageBase {
  originalFilename?: string;
  imageId: string;
  imageType: typeof ImageTypes.topHeadlineImageSmall;
}

export interface AppLibraryImage extends ImageBase {
  imageType: typeof ImageTypes.appLibraryImage;
  imageId: string;
  originalFilename?: string;
}

export interface CategoryImage extends ImageBase {
  imageType: typeof ImageTypes.categoryImage;
  imageId: string;
  originalFilename?: string;
}

export type Image =
  | PortalAvatar
  | PortalLogo
  | ProfileImage
  | ProfileImageSmall
  | TeaserImage
  | TitleImage
  | ContentImage
  | TopHeadlineImage
  | TopHeadlineImageSmall
  | ImageGallery
  | AppLibraryImage
  | CategoryImage;

export type ImageTypes = Image['imageType'];

export function isPortalImage(image: unknown): image is PortalImageBase {
  switch ((image as Image).imageType) {
    case 'portalAvatar':
    case 'portalLogo':
      return true;
    default:
      return false;
  }
}

export function isContentImage(image: unknown): image is ContentImageBase {
  switch ((image as Image).imageType) {
    case 'teaserImage':
    case 'titleImage':
    case 'contentImage':
    case 'topHeadlineImage':
    case 'topHeadlineImageSmall':
    case 'imageGalleryImage':
      return true;
    default:
      return false;
  }
}
